import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type AdminUser,
  type BulkAction,
  type ImportResult,
  type Role,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import DeviceManager from "../components/DeviceManager";

// Trainer admin page (spec §6.4, §7.4): user management + device restriction.

const inputCls =
  "rounded bg-slate-900 px-2 py-1.5 text-sm text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500";
const btnCls =
  "rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-50";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [enforcement, setEnforcement] = useState<boolean | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deviceUser, setDeviceUser] = useState<AdminUser | null>(null);

  // create-user form
  const [nu, setNu] = useState({
    username: "",
    display_name: "",
    password: "",
    role: "student" as Role,
    group_name: "",
  });
  // csv import
  const [csv, setCsv] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  // bulk
  const [bulkGroup, setBulkGroup] = useState("");

  const fail = useCallback((e: unknown, fallback = "Something went wrong.") => {
    setError(e instanceof ApiError ? e.message : fallback);
  }, []);

  const refresh = useCallback(
    (group?: string) => {
      setError(null);
      api
        .adminListUsers(group || undefined)
        .then(setUsers)
        .catch((e) => fail(e, "Could not load users."));
    },
    [fail],
  );

  useEffect(() => {
    refresh();
    api
      .adminGetSettings()
      .then((s) => setEnforcement(s.device_enforcement))
      .catch(() => setEnforcement(null));
  }, [refresh]);

  async function toggleEnforcement() {
    if (enforcement === null) return;
    try {
      const s = await api.adminSetSettings(!enforcement);
      setEnforcement(s.device_enforcement);
      setNotice(`Device restriction ${s.device_enforcement ? "ON" : "OFF"}.`);
    } catch (e) {
      fail(e);
    }
  }

  async function createUser(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.adminCreateUser({
        username: nu.username,
        display_name: nu.display_name,
        password: nu.password,
        role: nu.role,
        group_name: nu.group_name.trim() || null,
      });
      setNu({ username: "", display_name: "", password: "", role: "student", group_name: "" });
      setNotice("User created.");
      refresh(groupFilter);
    } catch (e) {
      fail(e, "Could not create user.");
    }
  }

  async function resetPassword(u: AdminUser) {
    const pw = window.prompt(`New password for "${u.username}" (min 8 chars):`);
    if (pw === null) return;
    try {
      await api.adminUpdateUser(u.id, { new_password: pw });
      setNotice(`Password reset for ${u.username}.`);
    } catch (e) {
      fail(e, "Could not reset password (min 8 characters).");
    }
  }

  async function toggleActive(u: AdminUser) {
    try {
      await api.adminUpdateUser(u.id, { is_active: !u.is_active });
      refresh(groupFilter);
    } catch (e) {
      fail(e);
    }
  }

  async function toggleDeviceCheck(u: AdminUser, checked: boolean) {
    try {
      await api.adminUpdateUser(u.id, { device_check: checked });
      setUsers((prev) =>
        prev ? prev.map((x) => (x.id === u.id ? { ...x, device_check: checked } : x)) : prev,
      );
    } catch (e) {
      fail(e);
    }
  }

  async function resetDevices(u: AdminUser) {
    if (
      !window.confirm(
        `Reset device(s) for "${u.username}"? They will need to re-register and be re-approved on next login (e.g. after switching computers).`,
      )
    )
      return;
    try {
      const res = await api.adminResetDevices(u.id);
      setNotice(`Reset ${res.deleted} device(s) for ${u.username}.`);
      refresh(groupFilter);
    } catch (e) {
      fail(e);
    }
  }

  async function removeUser(u: AdminUser) {
    if (
      !window.confirm(
        `Delete "${u.username}"? This removes their scripts, submissions and devices. This cannot be undone.`,
      )
    )
      return;
    try {
      await api.adminDeleteUser(u.id);
      setNotice(`Deleted ${u.username}.`);
      refresh(groupFilter);
    } catch (e) {
      fail(e);
    }
  }

  async function importCsv() {
    setError(null);
    try {
      const res = await api.adminImportUsers(csv);
      setImportResult(res);
      setNotice(`Imported ${res.created.length}, skipped ${res.skipped.length}.`);
      refresh(groupFilter);
    } catch (e) {
      fail(e, "Import failed.");
    }
  }

  async function bulk(action: BulkAction) {
    const g = bulkGroup.trim();
    if (!g) return;
    const verb =
      action === "delete"
        ? "DELETE all users in"
        : action === "deactivate"
          ? "deactivate"
          : "reactivate";
    if (!window.confirm(`${verb} group "${g}"?`)) return;
    try {
      const res = await api.adminBulk(action, g);
      setNotice(`${action}: ${res.affected} user(s) in "${g}".`);
      refresh(groupFilter);
    } catch (e) {
      fail(e);
    }
  }

  return (
    <div className="min-h-full bg-slate-900 text-slate-100">
      <header className="flex items-center justify-between border-b border-slate-700 bg-slate-800 px-4 py-2">
        <div className="flex items-center gap-4">
          <span className="font-semibold">Admin — Users &amp; Devices</span>
          <Link to="/" className="text-sm text-sky-300 hover:underline">
            ← Simulator
          </Link>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <span>
            {user?.display_name} ({user?.role})
          </span>
          <button type="button" onClick={logout} className={btnCls}>
            Log out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl space-y-6 p-4">
        {error && (
          <div className="rounded bg-red-950/60 px-3 py-2 text-sm text-red-300">{error}</div>
        )}
        {notice && (
          <div className="flex items-center justify-between rounded bg-emerald-950/50 px-3 py-2 text-sm text-emerald-200">
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        )}

        {/* Device enforcement */}
        <section className="rounded border border-slate-700 bg-slate-800 p-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Device restriction</h2>
              <p className="text-xs text-slate-400">
                Master switch. When ON, each student whose{" "}
                <span className="text-slate-300">“Check device”</span> box is ticked (below) may
                sign in only from a device you have approved. Untick a student to let them use any
                device. Admins and trainers are always exempt.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleEnforcement}
              disabled={enforcement === null}
              className={`rounded px-4 py-2 text-sm font-semibold ${
                enforcement
                  ? "bg-emerald-600 hover:bg-emerald-500"
                  : "bg-slate-600 hover:bg-slate-500"
              } disabled:opacity-50`}
            >
              {enforcement === null ? "…" : enforcement ? "ON" : "OFF"}
            </button>
          </div>
        </section>

        {/* Create user */}
        <section className="rounded border border-slate-700 bg-slate-800 p-3">
          <h2 className="mb-2 font-semibold">Create user</h2>
          <form onSubmit={createUser} className="flex flex-wrap items-end gap-2">
            <Field label="Username">
              <input
                className={inputCls}
                value={nu.username}
                onChange={(e) => setNu({ ...nu, username: e.target.value })}
                required
              />
            </Field>
            <Field label="Display name">
              <input
                className={inputCls}
                value={nu.display_name}
                onChange={(e) => setNu({ ...nu, display_name: e.target.value })}
              />
            </Field>
            <Field label="Password (min 8)">
              <input
                className={inputCls}
                type="text"
                value={nu.password}
                onChange={(e) => setNu({ ...nu, password: e.target.value })}
                required
              />
            </Field>
            <Field label="Role">
              <select
                className={inputCls}
                value={nu.role}
                onChange={(e) => setNu({ ...nu, role: e.target.value as Role })}
              >
                <option value="student">student</option>
                <option value="trainer">trainer</option>
                <option value="admin">admin</option>
              </select>
            </Field>
            <Field label="Group">
              <input
                className={inputCls}
                value={nu.group_name}
                onChange={(e) => setNu({ ...nu, group_name: e.target.value })}
              />
            </Field>
            <button
              type="submit"
              className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500"
            >
              Create
            </button>
          </form>
        </section>

        {/* CSV import */}
        <section className="rounded border border-slate-700 bg-slate-800 p-3">
          <h2 className="mb-1 font-semibold">Bulk import (CSV)</h2>
          <p className="mb-2 text-xs text-slate-400">
            One student per line: <code>username,display_name,password[,group]</code>. Lines
            starting with <code>#</code> are ignored. Duplicates and bad rows are reported, never
            overwritten.
          </p>
          <textarea
            className={`${inputCls} h-24 w-full font-mono`}
            placeholder="alice,Alice Tan,password123,2026-A&#10;bob,Bob Lee,password123"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3">
            <button type="button" className={btnCls} onClick={importCsv} disabled={!csv.trim()}>
              Import
            </button>
            {importResult && (
              <span className="text-xs text-slate-400">
                created {importResult.created.length}, skipped {importResult.skipped.length}
              </span>
            )}
          </div>
          {importResult && importResult.skipped.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-amber-300">
              {importResult.skipped.map((s, i) => (
                <li key={i}>
                  • {s.username || "(blank)"} — {s.reason}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Group bulk actions */}
        <section className="rounded border border-slate-700 bg-slate-800 p-3">
          <h2 className="mb-2 font-semibold">Group actions</h2>
          <div className="flex flex-wrap items-center gap-2">
            <input
              className={inputCls}
              placeholder="group name"
              value={bulkGroup}
              onChange={(e) => setBulkGroup(e.target.value)}
            />
            <button
              type="button"
              className={btnCls}
              onClick={() => bulk("deactivate")}
              disabled={!bulkGroup.trim()}
            >
              Deactivate all
            </button>
            <button
              type="button"
              className={btnCls}
              onClick={() => bulk("reactivate")}
              disabled={!bulkGroup.trim()}
            >
              Reactivate all
            </button>
            <button
              type="button"
              className="rounded bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
              onClick={() => bulk("delete")}
              disabled={!bulkGroup.trim()}
            >
              Delete all
            </button>
          </div>
        </section>

        {/* Users table */}
        <section className="rounded border border-slate-700 bg-slate-800 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="font-semibold">Users {users ? `(${users.length})` : ""}</h2>
            <div className="flex items-center gap-2">
              <input
                className={inputCls}
                placeholder="filter by group"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refresh(groupFilter)}
              />
              <button type="button" className={btnCls} onClick={() => refresh(groupFilter)}>
                Filter
              </button>
              <button
                type="button"
                className={btnCls}
                onClick={() => {
                  setGroupFilter("");
                  refresh();
                }}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded border border-slate-700">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-2 py-1">Username</th>
                  <th className="px-2 py-1">Name</th>
                  <th className="px-2 py-1">Role</th>
                  <th className="px-2 py-1">Group</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Check device</th>
                  <th className="px-2 py-1">Devices</th>
                  <th className="px-2 py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users === null ? (
                  <tr>
                    <td colSpan={8} className="px-2 py-3 text-slate-400">
                      Loading…
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-2 py-3 text-slate-400">
                      No users.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-700">
                      <td className="px-2 py-1 font-mono">
                        {u.username}
                        {u.id === user?.id && (
                          <span className="ml-1 text-xs text-slate-500">(you)</span>
                        )}
                      </td>
                      <td className="px-2 py-1">{u.display_name}</td>
                      <td className="px-2 py-1">{u.role}</td>
                      <td className="px-2 py-1 text-slate-400">{u.group_name ?? "—"}</td>
                      <td className="px-2 py-1">
                        {u.is_active ? (
                          <span className="text-emerald-400">active</span>
                        ) : (
                          <span className="text-red-400">inactive</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {u.role === "student" ? (
                          <input
                            type="checkbox"
                            checked={u.device_check}
                            onChange={(e) => toggleDeviceCheck(u, e.target.checked)}
                            title="When ticked (and global restriction is ON), this student may sign in only from an approved device. Untick to allow any device."
                          />
                        ) : (
                          <span
                            className="text-xs text-slate-500"
                            title="Staff are never device-checked"
                          >
                            exempt
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          className="text-sky-300 hover:underline"
                          onClick={() => setDeviceUser(u)}
                        >
                          {u.device_count} ▸
                        </button>
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="rounded px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700"
                            onClick={() => resetPassword(u)}
                          >
                            Reset pw
                          </button>
                          <button
                            type="button"
                            className="rounded px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700"
                            onClick={() => toggleActive(u)}
                            disabled={u.id === user?.id}
                          >
                            {u.is_active ? "Deactivate" : "Reactivate"}
                          </button>
                          {u.role === "student" && (
                            <button
                              type="button"
                              className="rounded px-2 py-0.5 text-xs text-amber-300 hover:bg-slate-700"
                              onClick={() => resetDevices(u)}
                              title="Remove all approved devices so the student re-registers"
                            >
                              Reset device
                            </button>
                          )}
                          <button
                            type="button"
                            className="rounded px-2 py-0.5 text-xs text-red-400 hover:bg-red-950/60"
                            onClick={() => removeUser(u)}
                            disabled={u.id === user?.id}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {deviceUser && (
        <DeviceManager
          user={deviceUser}
          onClose={() => {
            setDeviceUser(null);
            refresh(groupFilter);
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-400">
      {label}
      {children}
    </label>
  );
}
